import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminPaymentConfig() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [formData, setFormData] = useState({
    provider: 'cashfree',
    app_id: '',
    secret_key: '',
    environment: 'test',
    is_active: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/payment-config`);
      setConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch configs:', error);
      toast.error('Failed to load payment configs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post(`${API_URL}/api/admin/payment-config`, formData);
      toast.success('Payment configuration saved!');
      setShowForm(false);
      setFormData({ provider: 'cashfree', app_id: '', secret_key: '', environment: 'test', is_active: true });
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleDelete = async (configId) => {
    if (window.confirm('Delete this payment configuration?')) {
      try {
        await axiosInstance.delete(`${API_URL}/api/admin/payment-config/${configId}`);
        toast.success('Configuration deleted');
        fetchConfigs();
      } catch (error) {
        toast.error('Failed to delete configuration');
      }
    }
  };

  const toggleSecretVisibility = (configId) => {
    setShowSecrets(prev => ({ ...prev, [configId]: !prev[configId] }));
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payment Gateway Configuration</h1>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Configure Cashfree and other payment providers</p>
            </div>
            <Button className="btn-primary w-full sm:w-auto" onClick={() => setShowForm(!showForm)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Configuration
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>New Payment Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Provider</Label>
                    <select
                      value={formData.provider}
                      onChange={(e) => setFormData({...formData, provider: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="cashfree">Cashfree</option>
                      <option value="razorpay">Razorpay</option>
                      <option value="paytm">Paytm</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Environment</Label>
                    <select
                      value={formData.environment}
                      onChange={(e) => setFormData({...formData, environment: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="test">Test</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>App ID / Client ID *</Label>
                  <Input
                    value={formData.app_id}
                    onChange={(e) => setFormData({...formData, app_id: e.target.value})}
                    placeholder="Enter App ID"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secret Key *</Label>
                  <Input
                    type="password"
                    value={formData.secret_key}
                    onChange={(e) => setFormData({...formData, secret_key: e.target.value})}
                    placeholder="Enter Secret Key"
                    required
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                <div className="flex space-x-4">
                  <Button type="submit" className="btn-primary">Save Configuration</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {configs.map((config) => (
            <Card key={config.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-4">
                      <h3 className="text-xl font-bold capitalize">{config.provider}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.environment === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {config.environment.toUpperCase()}
                      </span>
                      {config.is_active && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">ACTIVE</span>}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">App ID:</span>
                        <p className="font-mono text-sm">{config.app_id}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Secret Key:</span>
                        <div className="flex items-center space-x-2">
                          <p className="font-mono text-sm">
                            {showSecrets[config.id] ? config.secret_key : '••••••••••••••••'}
                          </p>
                          <button
                            onClick={() => toggleSecretVisibility(config.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {showSecrets[config.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(config.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {configs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No payment configurations yet</p>
              <Button className="btn-primary" onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Configuration
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
